import * as React from 'react';

import { faEdit } from '@fortawesome/pro-regular-svg-icons';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import * as z from 'zod';

import ReservationForm from 'src/features/reservations/components/forms/ReservationForm';
import { useUpdateReservationVehicleStay } from 'src/features/reservations/hooks/useUpdateReservationVehicleStay';
import { reservationMessages } from 'src/features/reservations/utils/messages';
import { useParking } from 'src/providers/ParkingProvider';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { toast } from 'src/registry/hooks/UseToast';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogDescription,
} from 'src/registry/ui/Dialog';
import { generalErrorMessages, generalMessages, validationMessagesMap } from 'src/utils/messages';

import type { ReservationsVehicleStayList } from 'src/features/reservations/types/reservationType';

interface EditReservationModalProps extends React.HTMLAttributes<HTMLDivElement> {
    selectedReservation?: ReservationsVehicleStayList;
    inParking?: boolean;
    refetch: () => void;
}

export type EditReservationFormSchema = z.infer<typeof editReservationFormSchema>;

const editReservationFormSchema = z.object({
    license_plate: z.string().nonempty({ message: validationMessagesMap.validationErrorRequired }),
    client_email: z.string().email({ message: validationMessagesMap.validationErrorEmail }),
    client_name: z.string().min(3).max(50),
    client_phone: z.string(),
});

const EditReservationModal = ({ selectedReservation, inParking, refetch, children }: EditReservationModalProps) => {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();
    const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false);

    const form = useForm<EditReservationFormSchema>({
        resolver: zodResolver(editReservationFormSchema),
        mode: 'onChange',
        defaultValues: {},
    });

    React.useEffect(() => {
        if (selectedReservation) {
            form.reset({
                license_plate: selectedReservation.license_plate || '',
                client_email: selectedReservation.client_email || '',
                client_name: selectedReservation.client_name || '',
                client_phone: selectedReservation.client_phone || '',
            });
        }
    }, [selectedReservation]);

    const reservationNumber = selectedReservation?.number;

    const { mutate: editReservation } = useUpdateReservationVehicleStay({
        onSuccess: () => {
            void refetch();
            setIsModalOpen(false);
            toast({
                title: formatMessage(reservationMessages.editReservationSuccess),
                description: formatMessage(reservationMessages.editReservationSuccessDetails, { reservationNumber }),
            });
        },
        onError: (error) => {
            const errorData = error.response?.data;
            const errorCode = errorData?.code ?? formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorData?.message ?? formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const onSubmit = (values: EditReservationFormSchema) => {
        const dirtyFields = form.formState.dirtyFields;

        const editedFields = Object.keys(dirtyFields).reduce(
            (acc, key) => {
                const typedKey = key as keyof EditReservationFormSchema;
                acc[typedKey] = values[typedKey];
                return acc;
            },
            {} as Partial<Record<keyof EditReservationFormSchema, any>>,
        );
        const submitData = {
            parkingId: parkingId,
            reservationId: selectedReservation?.id || '',
            ...editedFields,
        };
        editReservation(submitData);
    };

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) {
            form.reset();
        }
        setIsModalOpen(isOpen);
    };

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={faEdit} />
                        <div>
                            <DialogTitle>{formatMessage(reservationMessages.editReservation)}</DialogTitle>
                            <DialogDescription>
                                {formatMessage(reservationMessages.editReservationDetails)}
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <ReservationForm form={form} onSubmit={form.handleSubmit(onSubmit)} inParking={inParking} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1" onClick={() => form.reset()}>
                            {formatMessage(generalMessages.cancel)}
                        </Button>
                    </DialogClose>
                    <Button
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                        disabled={!form.formState.isDirty || !form.formState.isValid}
                    >
                        {formatMessage(generalMessages.saveChanges)}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default EditReservationModal;
